import { useState } from "react";

const slices = [
  { label: "Community",         pct: 39, color: "#FF5050", emoji: "🫂" },
  { label: "Therapy & Healing", pct: 20, color: "#E98B37", emoji: "💛" },
  { label: "Revenge Marketing", pct: 15, color: "#8c52ff", emoji: "🔥" },
  { label: "Commitment Vault",  pct: 10, color: "#ffde59", emoji: "🔒" },
  { label: "CEX Reserve",       pct: 10, color: "#c1ff72", emoji: "💎" },
  { label: "Ritual Burn",       pct: 6, color: "#00bf63", emoji: "💀" },
];

function buildSlices(data) {
  let cumulative = 0;
  return data.map((s) => {
    const start = cumulative;
    cumulative += s.pct;
    const startAngle = (start / 100) * 360 - 90;
    const endAngle   = (cumulative / 100) * 360 - 90;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const cx = 160, cy = 160, r = 140;
    const x1 = cx + r * Math.cos(toRad(startAngle));
    const y1 = cy + r * Math.sin(toRad(startAngle));
    const x2 = cx + r * Math.cos(toRad(endAngle));
    const y2 = cy + r * Math.sin(toRad(endAngle));
    const large = endAngle - startAngle > 180 ? 1 : 0;
    const midAngle = toRad((startAngle + endAngle) / 2);
    const lx = cx + r * 0.65 * Math.cos(midAngle);
    const ly = cy + r * 0.65 * Math.sin(midAngle);
    return { ...s, path: `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`, lx, ly };
  });
}

function PieChart({ hovered, setHovered, computed, id }) {
  const filterId = `slice-shadow-${id}`;
  return (
    <div
      className="rounded-full border-[5px] border-black flex-shrink-0"
      style={{ boxShadow: "6px 8px 0 #000", background: "#fff", padding: "8px" }}
    >
      <svg width="300" height="300" viewBox="0 0 320 320">
        <defs>
          <filter id={filterId} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="3" stdDeviation="2" floodColor="#000" floodOpacity="0.35" />
          </filter>
        </defs>
        {computed.map((s, i) => (
          <g key={i}>
            <path
              d={s.path}
              fill={s.color}
              stroke="#000"
              strokeWidth={hovered === i ? "4" : "3"}
              filter={`url(#${filterId})`}
              transform={hovered === i ? `translate(${(160 - s.lx) * -0.07}, ${(160 - s.ly) * -0.07})` : ""}
              style={{ transition: "transform 0.2s, stroke-width 0.15s", cursor: "pointer" }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
            {s.pct >= 10 && (
              <text
                x={s.lx} y={s.ly}
                textAnchor="middle" dominantBaseline="middle"
                fontSize="15" fontWeight="900" fontFamily="sans-serif"
                fill="#fff" stroke="#000" strokeWidth="3" paintOrder="stroke"
                style={{ pointerEvents: "none" }}
              >
                {s.pct}%
              </text>
            )}
          </g>
        ))}
        <circle cx="160" cy="160" r="55" fill="white" stroke="#000" strokeWidth="4" />
        <text x="160" y="154" textAnchor="middle" fontSize="13" fontWeight="900" fontFamily="sans-serif" fill="#000">FMEX</text>
        <text x="160" y="172" textAnchor="middle" fontSize="11" fontFamily="sans-serif" fill="#888">100%</text>
      </svg>
    </div>
  );
}

/* Reusable info card */
function InfoCard({ label, value, emoji }) {
  return (
    <div
      className="rounded-2xl border-[3px] border-black px-5 py-3 text-center"
      style={{ background: "#fff", boxShadow: "0 5px 0 #000" }}
    >
      <div className="font-comfortaa text-[8px] font-bold text-gray-500 uppercase tracking-widest mb-1">
        {emoji} {label}
      </div>
      <div className="font-darumadrop text-base md:text-lg text-[#222]">{value}</div>
    </div>
  );
}

export default function Pettynomics() {
  const [hovered, setHovered] = useState(null);
  const computed = buildSlices(slices);

  return (
    <section id="pettynomics" className="relative w-full py-20 md:py-36 overflow-hidden">

      <div
        className="relative mx-auto max-w-5xl px-6 py-10 md:p-20"
        style={{ background: "rgba(190,234,255,0)" }}
      >
        {/* Desktop card background */}
        <div
          className="hidden md:block absolute inset-0 rounded-3xl shadow-[0_0_0_6px_white] border-[5px]"
          style={{
            background: "#BEEAFF",
            borderImage: "linear-gradient(45deg,#ff00ff,#00eaff,#ff9900,#ff00c8) 1",
            borderImageSlice: 1,
          }}
        />

        <div className="relative">
          <h2 className="text-center font-darumadrop text-4xl md:text-5xl font-black tracking-wide text-[#222] drop-shadow-sm mb-10">
            PETTYNOMICS
          </h2>

          {/* ── MOBILE layout ── */}
          <div className="flex md:hidden flex-col items-center gap-8">

            <PieChart hovered={hovered} setHovered={setHovered} computed={computed} id="mobile" />

            {/* 2-column legend */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 w-full">
              {computed.map((s, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 rounded-2xl border-[3px] border-black px-3 py-2"
                  style={{
                    background: hovered === i ? s.color : "#fff",
                    boxShadow: "0 4px 0 #000",
                  }}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <span className="w-3 h-3 rounded-full border-2 border-black flex-shrink-0" style={{ background: s.color }} />
                  <span className="font-comfortaa font-bold text-xs leading-tight flex-1" style={{ color: hovered === i ? "#fff" : "#1d1d1d" }}>
                    {s.label}
                  </span>
                  <span className="font-darumadrop text-base flex-shrink-0" style={{ color: hovered === i ? "#fff" : s.color }}>
                    {s.pct}%
                  </span>
                </div>
              ))}
            </div>

            {/* Info cards — mobile */}
            <div className="grid grid-cols-2 gap-4 w-full">
              <InfoCard emoji="🏦" label="Total Supply" value="1,000,000,000" />
              <InfoCard emoji="🔑" label="Token Address" value="Coming Soon" />
            </div>
          </div>

          {/* ── DESKTOP layout ── */}
          <div className="hidden md:flex items-center justify-center gap-24">

            {/* Left: legend */}
            <div className="space-y-3 flex-shrink-0">
              {computed.map((s, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered(null)}
                  className="grid grid-cols-[50px_20px_1fr] items-center gap-4 rounded-2xl border-[3px] border-black px-4 py-2 cursor-default"
                  style={{
                    background: hovered === i ? s.color : "#fff",
                    boxShadow: "0 4px 0 #000",
                    transform: hovered === i ? "translateY(2px)" : "translateY(0)",
                    transition: "background 0.15s, transform 0.15s",
                  }}
                >
                  <span className="font-darumadrop text-2xl font-black" style={{ color: hovered === i ? "#fff" : s.color }}>
                    {s.pct}%
                  </span>
                  <span className="w-4 h-4 rounded-full border-2 border-black flex-shrink-0" style={{ background: s.color }} />
                  <span className="font-comfortaa font-bold text-base" style={{ color: hovered === i ? "#fff" : "#1d1d1d" }}>
                    {s.emoji} {s.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Right: token info + pie + supply */}
            <div className="flex flex-col items-center gap-5 flex-shrink-0">

              <div className="flex gap-4">
               <InfoCard emoji="🔑" label="Token Address" value="Coming Soon" />
               <InfoCard emoji="🏦" label="Total Supply" value="1,000,000,000" />
             </div>

              <PieChart hovered={hovered} setHovered={setHovered} computed={computed} id="desktop" />

            </div>
          </div>

        </div>
      </div>
    </section>
  );
}